import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getRestaurantDevices,
  createDevice,
  updateDevice,
  deleteDevice,
  getAllTills,
  acceptDevice,
  blockDevice,
  unblockDevice,
  switchDevice,
  toggleDeviceStatus,
  updatePaymentAlertTime,
} from "@/api/new-api/settings/device-management";

export const fetchDeviceManagement = createAsyncThunk(
  "deviceManagement/fetchList",
  async ({ restaurantId, page = 1, per_page = 10 }, { rejectWithValue }) => {
    try {
      const res = await getRestaurantDevices(restaurantId, { page, per_page });
      if (res?.status === "success") return res.data;
      return rejectWithValue(res?.message || "Failed to fetch devices");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch devices");
    }
  },
);

export const createDeviceThunk = createAsyncThunk(
  "deviceManagement/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await createDevice(payload);
      if (res?.status === "success") return res.data?.device || res.data;
      return rejectWithValue(res?.message || "Failed to create device");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to create device");
    }
  },
);

export const updateDeviceThunk = createAsyncThunk(
  "deviceManagement/update",
  async ({ deviceId, payload }, { rejectWithValue }) => {
    try {
      const res = await updateDevice(deviceId, payload);
      if (res?.status === "success") return res.data?.device || res.data;
      return rejectWithValue(res?.message || "Failed to update device");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update device");
    }
  },
);

export const deleteDeviceThunk = createAsyncThunk(
  "deviceManagement/delete",
  async (deviceId, { rejectWithValue }) => {
    try {
      const res = await deleteDevice(deviceId);
      if (res?.status === "success") return deviceId;
      return rejectWithValue(res?.message || "Failed to delete device");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete device");
    }
  },
);

export const acceptDeviceThunk = createAsyncThunk(
  "deviceManagement/accept",
  async (deviceId, { rejectWithValue }) => {
    try {
      const res = await acceptDevice(deviceId);
      if (res?.status === "success") return res.data?.device || res.data;
      return rejectWithValue(res?.message || "Failed to accept device");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to accept device");
    }
  },
);

export const blockDeviceThunk = createAsyncThunk(
  "deviceManagement/block",
  async (deviceId, { rejectWithValue }) => {
    try {
      const res = await blockDevice(deviceId);
      if (res?.status === "success") return res.data?.device || res.data;
      return rejectWithValue(res?.message || "Failed to block device");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to block device");
    }
  },
);

export const unblockDeviceThunk = createAsyncThunk(
  "deviceManagement/unblock",
  async (deviceId, { rejectWithValue }) => {
    try {
      const res = await unblockDevice(deviceId);
      if (res?.status === "success") return res.data?.device || res.data;
      return rejectWithValue(res?.message || "Failed to unblock device");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to unblock device");
    }
  },
);

export const fetchAllTillsThunk = createAsyncThunk(
  "deviceManagement/fetchAllTills",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const res = await getAllTills(restaurantId);
      if (res?.status === "success") return res.data?.tills || [];
      return rejectWithValue(res?.message || "Failed to fetch tills");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch tills");
    }
  },
);

export const switchDeviceThunk = createAsyncThunk(
  "deviceManagement/switch",
  async (
    { restaurantId, deviceId, target_device_id, note },
    { rejectWithValue },
  ) => {
    try {
      const res = await switchDevice(restaurantId, deviceId, { target_device_id, note });
      if (res?.status === "success") {
        return {
          device: res.data?.device || res.data,
          sourceDeviceId: deviceId,
          targetDeviceId: target_device_id,
        };
      }
      return rejectWithValue(res?.message || "Failed to switch device");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to switch device");
    }
  },
);

export const toggleDeviceStatusThunk = createAsyncThunk(
  "deviceManagement/toggleStatus",
  async (deviceId, { rejectWithValue }) => {
    try {
      const res = await toggleDeviceStatus(deviceId);
      if (res?.status === "success") return res.data?.device || res.data;
      return rejectWithValue(res?.message || "Failed to toggle device status");
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to toggle device status");
    }
  },
);

export const updatePaymentAlertTimeThunk = createAsyncThunk(
  "deviceManagement/updatePaymentAlertTime",
  async ({ deviceId, payment_alert_time }, { rejectWithValue }) => {
    try {
      const res = await updatePaymentAlertTime(deviceId, payment_alert_time);
      if (res?.status === "success") {
        return res.data?.device || { id: deviceId, payment_alert_time };
      }
      return rejectWithValue(
        res?.message || "Failed to update payment alert time",
      );
    } catch (err) {
      return rejectWithValue(
        err?.message || "Failed to update payment alert time",
      );
    }
  },
);

const initialState = {
  devices: [],
  tills: [],
  pagination: null,
  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  acceptLoading: false,
  blockLoading: false,
  unblockLoading: false,
  tillsLoading: false,
  switchLoading: false,
  toggleLoading: false,
  paymentAlertTimeLoading: false,
  success: null,
  error: null,
};

const deviceManagementSlice = createSlice({
  name: "deviceManagement",
  initialState,
  reducers: {
    clearDeviceManagementMessages: (state) => {
      state.success = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeviceManagement.pending, (state) => {
        state.getLoading = true;
        state.error = null;
      })
      .addCase(fetchDeviceManagement.fulfilled, (state, action) => {
        state.getLoading = false;
        state.devices = action.payload?.devices || [];
        state.pagination = action.payload?.pagination || null;
      })
      .addCase(fetchDeviceManagement.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
      })
      .addCase(createDeviceThunk.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createDeviceThunk.fulfilled, (state, action) => {
        state.createLoading = false;
        if (action.payload) {
          state.devices.unshift(action.payload);
        }
        state.success = "Device created successfully";
      })
      .addCase(createDeviceThunk.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      })
      .addCase(updateDeviceThunk.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateDeviceThunk.fulfilled, (state, action) => {
        state.updateLoading = false;
        const index = state.devices.findIndex((d) => d.id === action.payload?.id);
        if (index !== -1 && action.payload) {
          state.devices[index] = action.payload;
        }
        state.success = "Device updated successfully";
      })
      .addCase(updateDeviceThunk.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteDeviceThunk.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteDeviceThunk.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.devices = state.devices.filter((d) => d.id !== action.payload);
        state.success = "Device deleted successfully";
      })
      .addCase(deleteDeviceThunk.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      })
      .addCase(acceptDeviceThunk.pending, (state) => {
        state.acceptLoading = true;
        state.error = null;
      })
      .addCase(acceptDeviceThunk.fulfilled, (state, action) => {
        state.acceptLoading = false;
        const index = state.devices.findIndex((d) => d.id === action.payload?.id);
        if (index !== -1 && action.payload) {
          state.devices[index] = action.payload;
        }
        state.success = "Device accepted successfully";
      })
      .addCase(acceptDeviceThunk.rejected, (state, action) => {
        state.acceptLoading = false;
        state.error = action.payload;
      })
      .addCase(blockDeviceThunk.pending, (state) => {
        state.blockLoading = true;
        state.error = null;
      })
      .addCase(blockDeviceThunk.fulfilled, (state, action) => {
        state.blockLoading = false;
        const index = state.devices.findIndex((d) => d.id === action.payload?.id);
        if (index !== -1 && action.payload) {
          state.devices[index] = action.payload;
        }
        state.success = "Device blocked successfully";
      })
      .addCase(blockDeviceThunk.rejected, (state, action) => {
        state.blockLoading = false;
        state.error = action.payload;
      })
      .addCase(unblockDeviceThunk.pending, (state) => {
        state.unblockLoading = true;
        state.error = null;
      })
      .addCase(unblockDeviceThunk.fulfilled, (state, action) => {
        state.unblockLoading = false;
        const index = state.devices.findIndex((d) => d.id === action.payload?.id);
        if (index !== -1 && action.payload) {
          state.devices[index] = action.payload;
        }
        state.success = "Device unblocked successfully";
      })
      .addCase(unblockDeviceThunk.rejected, (state, action) => {
        state.unblockLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllTillsThunk.pending, (state) => {
        state.tillsLoading = true;
        state.error = null;
      })
      .addCase(fetchAllTillsThunk.fulfilled, (state, action) => {
        state.tillsLoading = false;
        state.tills = action.payload;
      })
      .addCase(fetchAllTillsThunk.rejected, (state, action) => {
        state.tillsLoading = false;
        state.error = action.payload;
      })
      .addCase(switchDeviceThunk.pending, (state) => {
        state.switchLoading = true;
        state.error = null;
      })
      .addCase(switchDeviceThunk.fulfilled, (state, action) => {
        state.switchLoading = false;
        const updatedDevice = action.payload?.device;
        const sourceDeviceId = action.payload?.sourceDeviceId;

        if (updatedDevice?.id) {
          const sourceIndex = state.devices.findIndex((d) => d.id === updatedDevice.id);
          if (sourceIndex !== -1) {
            state.devices[sourceIndex] = {
              ...state.devices[sourceIndex],
              ...updatedDevice,
            };
          }
        } else if (sourceDeviceId) {
          const sourceIndex = state.devices.findIndex((d) => d.id === sourceDeviceId);
          if (sourceIndex !== -1) {
            state.devices[sourceIndex] = {
              ...state.devices[sourceIndex],
              parent_device_id: action.payload?.targetDeviceId,
            };
          }
        }

        state.success = "Device switched successfully";
      })
      .addCase(switchDeviceThunk.rejected, (state, action) => {
        state.switchLoading = false;
        state.error = action.payload;
      })
      .addCase(toggleDeviceStatusThunk.pending, (state) => {
        state.toggleLoading = true;
        state.error = null;
      })
      .addCase(toggleDeviceStatusThunk.fulfilled, (state, action) => {
        state.toggleLoading = false;
        const updated = action.payload;
        if (updated?.id) {
          const idx = state.devices.findIndex((d) => d.id === updated.id);
          if (idx !== -1) state.devices[idx] = { ...state.devices[idx], ...updated };
        }
        state.success = "Device status updated successfully";
      })
      .addCase(toggleDeviceStatusThunk.rejected, (state, action) => {
        state.toggleLoading = false;
        state.error = action.payload;
      })
      .addCase(updatePaymentAlertTimeThunk.pending, (state) => {
        state.paymentAlertTimeLoading = true;
        state.error = null;
      })
      .addCase(updatePaymentAlertTimeThunk.fulfilled, (state, action) => {
        state.paymentAlertTimeLoading = false;
        const updated = action.payload;
        if (updated?.id) {
          const idx = state.devices.findIndex((d) => d.id === updated.id);
          if (idx !== -1) {
            state.devices[idx] = {
              ...state.devices[idx],
              ...updated,
              payment_alert_time: updated.payment_alert_time,
            };
          }
        }
        state.success = "Payment alert time updated successfully";
      })
      .addCase(updatePaymentAlertTimeThunk.rejected, (state, action) => {
        state.paymentAlertTimeLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearDeviceManagementMessages } = deviceManagementSlice.actions;
export default deviceManagementSlice.reducer;
